import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import type { Options as SanitizeSchema } from 'rehype-sanitize'

/**
 * What the assistant said, as markdown.
 *
 * <p>Not a nicety. A model writes markdown whether or not anything renders it — MiniMax answers
 * a balance question with `**Entitled:** 16.50 days` and a bullet list — and rendering that as
 * plain text shows a person literal asterisks and hyphens. The panel was doing exactly that.
 *
 * <p><b>Nothing here trusts the model.</b> The text arrives from a third party, by way of a
 * provider this product does not control, and it is rendered inside an authenticated session
 * that holds the user's own permissions. So:
 *
 * <ul>
 *   <li>{@code rehype-raw} is <em>not</em> used, so embedded HTML is never parsed — it stays
 *       text. That alone stops a script tag from ever becoming an element;
 *   <li>{@code rehype-sanitize} runs anyway, on a schema narrowed further than its default,
 *       because "we do not enable the dangerous plugin" is a property of today's code and the
 *       sanitiser is a property of the output;
 *   <li>links are restricted to http, https and mailto — the default schema permits several
 *       more — and carry {@code rel="noopener noreferrer nofollow"};
 *   <li>images are dropped entirely. An {@code <img src>} the model chooses is an outbound
 *       request to a URL the model chooses, made by the user's browser, on every render.
 * </ul>
 */

/**
 * The allowed shape of the output.
 *
 * <p>Built from rehype-sanitize's default rather than from nothing, so anything it learns to
 * strip in a later version is stripped here too; then narrowed. `img` is removed from the
 * element list, and the protocol list for `href` is cut to the three a leave balance could ever
 * need.
 */
const SCHEMA: SanitizeSchema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames ?? []).filter((tag) => tag !== 'img'),
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a ?? []), 'target', 'rel'],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
  },
}

/**
 * Every block uses logical properties, so the panel reads correctly in Arabic.
 *
 * <p>`ps-5` rather than `pl-5` on a list, `border-s` rather than `border-l` on a quote: in RTL
 * a bullet indent on the left is an indent on the wrong side, and it is the kind of thing that
 * looks fine to whoever wrote it and wrong to everybody who reads the product in Arabic. The
 * repo's ESLint rule bans the physical forms outright, which is why it cannot be got wrong here.
 */
const COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-2 last:mb-0">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-2 ms-1 list-disc space-y-0.5 ps-4 last:mb-0">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-2 ms-1 list-decimal space-y-0.5 ps-4 last:mb-0">{children}</ol>
  ),
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    // react-markdown marks a fenced block with a language class and leaves inline code bare,
    // which is the only way to tell them apart here.
    const fenced = typeof className === 'string' && className.startsWith('language-')
    return fenced ? (
      <code className="block whitespace-pre">{children}</code>
    ) : (
      <code className="rounded bg-[var(--color-page)] px-1 py-0.5 text-[0.9em]">{children}</code>
    )
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    // Its own scroller: a wide code block must not widen the panel or the page behind it.
    <pre className="mb-2 overflow-x-auto overscroll-x-contain rounded-[var(--radius-control)] bg-[var(--color-page)] p-2 text-xs last:mb-0">
      {children}
    </pre>
  ),
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="text-[var(--color-primary)] underline underline-offset-2"
    >
      {children}
    </a>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="mb-2 overflow-x-auto overscroll-x-contain last:mb-0">
      <table className="w-full text-xs">{children}</table>
    </div>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="border-b border-[var(--color-border)] py-1 pe-3 text-start font-medium">
      {children}
    </th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border-b border-[var(--color-border)] py-1 pe-3 align-top">{children}</td>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="mb-2 border-s-2 border-[var(--color-border)] ps-2 text-[var(--color-muted)] last:mb-0">
      {children}
    </blockquote>
  ),
  // A model that decides to write a heading should not out-shout the panel's own.
  h1: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-1 font-semibold">{children}</p>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-1 font-semibold">{children}</p>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-1 font-semibold">{children}</p>
  ),
}

export function AssistantMarkdown({ children }: { children: string }) {
  return (
    <div className="[&_strong]:font-semibold">
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, SCHEMA]]}
        components={COMPONENTS}
      >
        {children}
      </Markdown>
    </div>
  )
}
